/**
 * Where a part mounted on one of a shower's walls — its head, its valve, a
 * niche recessed into the wall, a bench against it — goes when the shower is
 * moved or resized (2026-10-30). It stays on THE WALL IT IS ON: the shower's
 * back (its local −Z) or either end (−X / +X), whichever face it is nearest,
 * at the same distance in front of (or, for a niche, into) that wall; along
 * the wall it keeps its place in proportion, so a centred part stays centred.
 * A part in a back corner — its footprint within 5 cm of the back and an end
 * wall, a corner shelf or seat (step 3a) — stays in that corner, at the same
 * distance from both.
 *
 * Every mover used to put every part on the back wall — the generator moves
 * the valve and head to an end wall when the back wall has a window, and a
 * resize dragged them back across. The ONE rule for the browser's resize
 * (`parentChildSystem.repositionShowerAccessories`), the cloud's
 * `resize_fixture` and its wall-shrink (`placeShower.repositionAccessories`).
 *
 * A shower's frame is the wall-mounted convention: local +Z = (sin ry, cos ry)
 * points out of the back wall into the shower, local +X = (cos ry, −sin ry),
 * `dimensions.x` its width along X and `dimensions.z` its depth along Z. Pure.
 */

const { ACCESSORY_TYPES } = require('../shower-systems');

/** The parts that hang on one of a shower's walls, its accessories included. */
const SHOWER_WALL_PART_TYPES = new Set([
  'showerhead',
  'shower-controls',
  'shower-control',
  'shower-niche',
  'shower-bench',
  ...ACCESSORY_TYPES,
]);

// a part whose footprint comes this close to two walls is in their corner
const CORNER_TOL_M = 0.05;

/**
 * How far `part`'s footprint reaches from its centre across `shower`'s
 * width (`u`) and depth (`v`), from its own size and turn.
 */
function halfExtentsIn(part, shower) {
  const turn = (part.rotation?.y ?? 0) - (shower.rotation?.y ?? 0);
  const hx = (part.dimensions?.x ?? 0) / 2;
  const hz = (part.dimensions?.z ?? 0) / 2;
  const c = Math.abs(Math.cos(turn));
  const s = Math.abs(Math.sin(turn));
  return { u: c * hx + s * hz, v: s * hx + c * hz };
}

/** `point` in `shower`'s frame: `u` across its width, `v` along its depth. */
function toShowerFrame(point, shower) {
  const ry = shower.rotation?.y ?? 0;
  const dx = point.x - shower.center.x;
  const dz = point.z - shower.center.z;
  return {
    u: dx * Math.cos(ry) - dz * Math.sin(ry),
    v: dx * Math.sin(ry) + dz * Math.cos(ry),
  };
}

/** Back out of `shower`'s frame to the plan. */
function fromShowerFrame({ u, v }, shower) {
  const ry = shower.rotation?.y ?? 0;
  return {
    x: shower.center.x + u * Math.cos(ry) + v * Math.sin(ry),
    z: shower.center.z - u * Math.sin(ry) + v * Math.cos(ry),
  };
}

/** A length scaled from `from` to `to`; unscaled when `from` is not a size. */
const scaled = (value, from, to) => (from > 0 ? (value * to) / from : value);

/**
 * `part`'s centre on `after` (the shower moved or resized), from where it is
 * on `before` — both `{ center, dimensions, rotation }`, the same turn.
 *
 * @returns {{x: number, y: number, z: number}}
 */
function followShowerWall(part, before, after) {
  const { u, v } = toShowerFrame(part.center, before);
  const w0 = before.dimensions?.x ?? 0;
  const d0 = before.dimensions?.z ?? 0;
  const w1 = after.dimensions?.x ?? 0;
  const d1 = after.dimensions?.z ?? 0;
  // the face it is nearest: the back, the left end or the right end
  const toBack = Math.abs(v + d0 / 2);
  const toLeft = Math.abs(u + w0 / 2);
  const toRight = Math.abs(u - w0 / 2);
  // a part in a back corner (a corner shelf, a seat) keeps its place
  // against both walls
  const reach = halfExtentsIn(part, before);
  const inBack = toBack - reach.v <= CORNER_TOL_M;
  const inLeft = toLeft - reach.u <= CORNER_TOL_M;
  const inRight = toRight - reach.u <= CORNER_TOL_M;
  let next;
  if (inBack && (inLeft || inRight)) {
    const endShift = (w0 - w1) / 2;
    next = {
      u:
        inLeft && (!inRight || toLeft <= toRight) ? u + endShift : u - endShift,
      v: v + (d0 - d1) / 2,
    };
  } else if (toBack <= toLeft && toBack <= toRight) {
    next = { u: scaled(u, w0, w1), v: v + (d0 - d1) / 2 };
  } else if (toLeft <= toRight) {
    next = { u: u + (w0 - w1) / 2, v: scaled(v, d0, d1) };
  } else {
    next = { u: u - (w0 - w1) / 2, v: scaled(v, d0, d1) };
  }
  const { x, z } = fromShowerFrame(next, after);
  return { x, y: part.center.y, z };
}

module.exports = { SHOWER_WALL_PART_TYPES, followShowerWall, toShowerFrame };
